import React, { useState } from 'react';
import { 
  WalletMinimalIcon,
  DollarSign, 
  Calculator,
  Plus,
  Search, 
  Filter,
  Edit3,
  Eye,
  Clock,
  TrendingDown,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Calendar 
} from 'lucide-react';

interface PayrollRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  position: string;
  baseSalary: number;
  bonus: number;
  latePenalty: number;
  otherDeductions: number;
  finalSalary: number;
  month: string;
  year: number;
  status: 'pending' | 'processed' | 'paid';
  lateCount: number;
}

interface PayrollModification {
  type: 'keterlambatan' | 'denda' | 'bonus';
  amount: number;
  reason: string;
}

export default function DashboardAplikasiGaji() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  // Sample data
  const payrollRecords: PayrollRecord[] = [
    {
      id: '1',
      employeeId: '1',
      employeeName: 'Ahmad Rizki Pratama',
      position: 'Software Developer',
      baseSalary: 8500000,
      bonus: 500000,
      latePenalty: 100000,
      otherDeductions: 50000,
      finalSalary: 8850000,
      month: 'February',
      year: 2024,
      status: 'processed',
      lateCount: 2
    },
    {
      id: '2',
      employeeId: '2',
      employeeName: 'Siti Nurhaliza',
      position: 'Marketing Specialist',
      baseSalary: 6500000,
      bonus: 300000,
      latePenalty: 0,
      otherDeductions: 0,
      finalSalary: 6800000,
      month: 'February',
      year: 2024,
      status: 'pending',
      lateCount: 0
    },
    {
      id: '3',
      employeeId: '3',
      employeeName: 'Budi Santoso',
      position: 'Finance Manager',
      baseSalary: 12000000,
      bonus: 1000000,
      latePenalty: 50000,
      otherDeductions: 100000,
      finalSalary: 12850000,
      month: 'February',
      year: 2024,
      status: 'paid',
      lateCount: 1
    }
  ];
  
  const [modifications, setModifications] = useState<PayrollModification[]>([
    { type: 'keterlambatan', amount: 50000, reason: 'Per hari keterlambatan' },
    { type: 'bonus', amount: 0, reason: 'Performance bonus' },
    { type: 'denda', amount: 0, reason: 'Denda lainnya' }
  ]);

  const filteredRecords = payrollRecords.filter(record => {
    const matchesSearch = record.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'processed':
        return (
          <span className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full flex items-center">
            <Calculator className="w-3 h-3 mr-1" />
            Diproses
          </span>
        );
      case 'paid':
        return (
          <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            Dibayar
          </span>
        );
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(amount);
  };

  const ProcessPayrollModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Proses Penggajian</h3>
          <button
            onClick={() => setShowProcessModal(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="space-y-6">
          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pilih Karyawan
            </label>
            <select
              value={selectedEmployee || ''}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Pilih Karyawan</option>
              <option value="1">Ahmad Rizki Pratama - Software Developer</option>
              <option value="2">Siti Nurhaliza - Marketing Specialist</option>
              <option value="3">Budi Santoso - Finance Manager</option>
            </select>
          </div>

          {/* Base Salary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Gaji Pokok</h4>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Gaji Pokok Bulanan:</span>
              <span className="font-semibold text-lg">{formatCurrency(8500000)}</span>
            </div>
          </div>

          {/* Modifications */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4">Modifikasi Gaji</h4>
            
            {/* Late Penalty */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <TrendingDown className="w-5 h-5 text-red-600 mr-2" />
                  <span className="font-medium text-red-800">Keterlambatan</span>
                </div>
                <span className="text-sm text-red-600">Pengurangan</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Jumlah Terlambat (hari)</label>
                  <input
                    type="number"
                    defaultValue="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Denda per Hari</label>
                  <input
                    type="number"
                    defaultValue="50000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div className="mt-2 text-right">
                <span className="text-red-700 font-medium">Total Denda: {formatCurrency(100000)}</span>
              </div>
            </div>

            {/* Bonus */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-medium text-green-800">Bonus</span>
                </div>
                <span className="text-sm text-green-600">Penambahan</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Jenis Bonus</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option>Performance Bonus</option>
                    <option>Attendance Bonus</option>
                    <option>Project Bonus</option>
                    <option>Lainnya</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Jumlah Bonus</label>
                  <input
                    type="number"
                    defaultValue="500000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="mt-2 text-right">
                <span className="text-green-700 font-medium">Total Bonus: {formatCurrency(500000)}</span>
              </div>
            </div>

            {/* Other Deductions */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-orange-600 mr-2" />
                  <span className="font-medium text-orange-800">Denda Lainnya</span>
                </div>
                <span className="text-sm text-orange-600">Pengurangan</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Keterangan</label>
                  <input
                    type="text"
                    placeholder="Misal: Kerusakan peralatan"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Jumlah Denda</label>
                  <input
                    type="number"
                    defaultValue="50000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div className="mt-2 text-right">
                <span className="text-orange-700 font-medium">Total Denda: {formatCurrency(50000)}</span>
              </div>
            </div>
          </div>

          {/* Final Calculation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-3">Perhitungan Akhir</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Gaji Pokok:</span>
                <span>{formatCurrency(8500000)}</span>
              </div>
              <div className="flex justify-between text-green-700">
                <span>Bonus:</span>
                <span>+ {formatCurrency(500000)}</span>
              </div>
              <div className="flex justify-between text-red-700">
                <span>Denda Keterlambatan:</span>
                <span>- {formatCurrency(100000)}</span>
              </div>
              <div className="flex justify-between text-red-700">
                <span>Denda Lainnya:</span>
                <span>- {formatCurrency(50000)}</span>
              </div>
              <hr className="border-blue-300" />
              <div className="flex justify-between text-lg font-bold text-blue-900">
                <span>Total Gaji:</span>
                <span>{formatCurrency(8850000)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              onClick={() => setShowProcessModal(false)}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Batal
            </button>
            <button
              onClick={() => {
                // Handle process logic here
                setShowProcessModal(false);
              }}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Proses Gaji
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <WalletMinimalIcon className="w-8 h-8 text-blue-600 mr-3" />
            <div>
                <div className="flex items-center">
                  <h1 className="text-3xl font-bold text-gray-800">Aplikasi Gaji</h1>
                  <p className="text-3xl font-bold text-green-800 pl-4">Pengembangan</p>
                </div>
                <p className="text-gray-600">Kelola penggajian dan modifikasi gaji karyawan</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Gaji Bulan Ini</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(payrollRecords.reduce((sum, record) => sum + record.finalSalary, 0))}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Sudah Diproses</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {payrollRecords.filter(r => r.status === 'processed' || r.status === 'paid').length}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {payrollRecords.filter(r => r.status === 'pending').length}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-xl">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Bonus</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatCurrency(payrollRecords.reduce((sum, record) => sum + record.bonus, 0))}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Cari karyawan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full md:w-64"
                />
              </div>

              {/* Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full md:w-48"
                >
                  <option value="all">Semua Status</option>
                  <option value="pending">Pending</option>
                  <option value="processed">Diproses</option>
                  <option value="paid">Dibayar</option>
                </select>
              </div>

              {/* Month/Year Selector */}
              <div className="flex space-x-2">
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="2">February</option>
                  <option value="1">January</option>
                  <option value="12">December</option>
                </select>
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3">
              <button className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <Calendar className="w-4 h-4 mr-2" />
                Export Payroll
              </button>
              <button
                onClick={() => setShowProcessModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Proses Gaji
              </button>
            </div>
          </div>
        </div>

        {/* Payroll Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Karyawan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gaji Pokok
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bonus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Denda
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Gaji
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{record.employeeName}</div>
                        <div className="text-sm text-gray-500">{record.position}</div>
                        {record.lateCount > 0 && (
                          <div className="text-xs text-red-600 flex items-center mt-1">
                            <Clock className="w-3 h-3 mr-1" />
                            {record.lateCount} hari terlambat
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(record.baseSalary)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {record.bonus > 0 ? `+${formatCurrency(record.bonus)}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {(record.latePenalty + record.otherDeductions) > 0 
                        ? `-${formatCurrency(record.latePenalty + record.otherDeductions)}`
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      {formatCurrency(record.finalSalary)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button className="text-blue-600 hover:text-blue-900 p-1 rounded">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="text-green-600 hover:text-green-900 p-1 rounded">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button className="text-purple-600 hover:text-purple-900 p-1 rounded">
                          <Calculator className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
